import type { UseFormRegister } from 'react-hook-form';

import { Collapsible } from '@repo/ui/Collapsible';
import { DialogRoot } from '@repo/ui/Dialog/DialogRoot';
import { Flex } from '@repo/ui/Flex';
import { Text } from '../../Language/Language';

import { RadioButton } from '../../RadioButton/RadioButton';
import type { WorkflowData } from '../../../workflow';
import type { DeliveryOption } from '../../../graphql/query/getDelivery';
import { SkeletonLoader } from '../../SkeletonLoader/SkeletonLoader';
import { EmptyMessage } from '../../EmptyMessage/EmptyMessage';
import { CollapsibleHeader } from '../../CollapsibleHeader/CollapsibleHeader';
import { LocationPreview } from '../../Locations/LocationPreview';
import {
  LocationPicker,
  LocationPickerTrigger,
} from '../../Locations/LocationPicker';
import { AddressForm } from '../../AddressForm/AddressForm';

import { getId, getLabel } from './helpers';
import s from './MethodTask.module.css';

const Loader = () => (
  <SkeletonLoader
    padding={['py-24', 'px-16']}
    itemHeight={30}
    layout={{
      repeat: 2,
      items: {
        default: [
          {
            columns: 1,
            rows: 1,
          },
          {
            columns: 7,
            rows: 1,
            startColumn: 2,
            startRow: 1,
          },
          {
            columns: 5,
            rows: 1,
            startColumn: 2,
            startRow: 2,
          },
          {
            columns: 2,
            rows: 1,
            startColumn: 11,
            startRow: 1,
          },
        ],
        mobile: [
          {
            columns: 2,
            rows: 1,
          },
          {
            columns: 6,
            rows: 1,
            startColumn: 3,
            startRow: 1,
          },
          {
            columns: 4,
            rows: 1,
            startColumn: 3,
            startRow: 2,
          },
          {
            columns: 3,
            rows: 1,
            startColumn: 10,
            startRow: 1,
          },
        ],
      },
    }}
  />
);

const MethodList = ({
  deliveryOptions,
  selectedMethod,
  isAddressRequired,
  countryCode,
  register,
}: {
  deliveryOptions: DeliveryOption[] | undefined;
  selectedMethod: string | undefined;
  isAddressRequired: boolean;
  countryCode: string;
  register?: UseFormRegister<WorkflowData>;
}) => {
  if (!deliveryOptions || deliveryOptions.length === 0) {
    return <EmptyMessage language={{ langKey: 'pdx.delivery.no_methods' }} />;
  }

  return (
    <Flex direction="column" gap="g-12">
      <Flex role="radiogroup" direction="column">
        {deliveryOptions.map(
          ({
            fees,
            id: setId,
            type,
            serviceProvider: { moniker, name, serviceType },
            packagingOption,
            placeholder,
            locations,
          }) => {
            const id = setId
              ? setId
              : getId({ moniker, type, packageType: packagingOption });
            const baseLabel = getLabel({
              id,
              name,
              packagingOption,
              serviceType,
            });
            const fee = fees[0];
            const hasLocations = locations && locations.length > 0;
            const hasContent =
              selectedMethod === id && (hasLocations || isAddressRequired);

            return (
              <RadioButton
                className={s.radio}
                key={id}
                id={id}
                value={id}
                appendedChildren={
                  hasContent && (
                    <Flex direction="column" gap="g-24" alignItems="center">
                      {hasLocations && (
                        <>
                          <LocationPreview placeholder={placeholder} />
                          <LocationPickerTrigger />
                        </>
                      )}
                      {isAddressRequired && (
                        <AddressForm
                          baseLabel="pdx.delivery.address"
                          name="returnMethod.address"
                          countryCode={countryCode}
                        />
                      )}
                    </Flex>
                  )
                }
                {...(register
                  ? register('returnMethod.type')
                  : { name: 'returnMethod.type' as const })}
              >
                <Flex
                  gap="g-16"
                  alignItems="center"
                  justifyContent="space-between"
                  className={s.radioContent}
                >
                  <Flex direction="column" gap="g-4">
                    <strong className={s.title}>
                      <Text langKey={baseLabel.map((e) => `${e}.Title`)} />
                    </strong>
                    <Text langKey={baseLabel.map((e) => `${e}.Description`)} />
                  </Flex>

                  {fee ? (
                    fee.amount.formattedString
                  ) : (
                    <Text
                      langKey={[
                        ...baseLabel.map((e) => `${e}.zero_fee_label`),
                        'pdx.delivery.zero_fee_label',
                      ]}
                    />
                  )}
                </Flex>
              </RadioButton>
            );
          },
        )}
      </Flex>
    </Flex>
  );
};

export interface ReturnMethodFormViewProps {
  isLoading: boolean;
  deliveryOptions?: DeliveryOption[];
  selectedMethod?: string;
  isAddressRequired: boolean;
  orderNumber?: string;
  countryCode: string;
  hasLocations: boolean;
  // Form integration
  register?: UseFormRegister<WorkflowData>;
}

/**
 * Pure presentational component for displaying return method selection.
 * Contains no hooks or business logic.
 * All data and callbacks provided via props.
 */
export const ReturnMethodFormView = ({
  isLoading,
  deliveryOptions,
  selectedMethod,
  isAddressRequired,
  orderNumber,
  countryCode,
  hasLocations,
  register,
}: ReturnMethodFormViewProps) => {
  // If we have only one delivery option and it's NONE, render nothing
  if (
    deliveryOptions &&
    deliveryOptions.length === 1 &&
    deliveryOptions[0]?.type === 'NONE'
  ) {
    return null;
  }

  return (
    <Flex direction="column">
      <h2 className={s.methodSelectionHeader}>
        <Text
          langKey="pdx.delivery.task.title"
          replacementTokens={{ order_number: orderNumber ?? '' }}
        />
      </h2>
      <Collapsible
        header={
          <CollapsibleHeader
            id="returnMethodsTitle"
            language={{ langKey: 'pdx.delivery.header' }}
          />
        }
      >
        <DialogRoot>
          {isLoading ? (
            <Loader />
          ) : (
            <MethodList
              deliveryOptions={deliveryOptions}
              selectedMethod={selectedMethod}
              isAddressRequired={isAddressRequired}
              countryCode={countryCode}
              register={register}
            />
          )}
          {hasLocations ? <LocationPicker /> : null}
        </DialogRoot>
      </Collapsible>
    </Flex>
  );
};
